import lightGallery from 'lightgallery/lightgallery.min';
import lgZoom from 'lightgallery/plugins/zoom/lg-zoom.min';
import lgVideo from 'lightgallery/plugins/video/lg-video.min';
import "lightgallery/scss/lightgallery-bundle.scss";
import {Hero} from "../sections/commons/hero";

// default starting volume for hero videos opened in the lightbox (0 to 1)
const DEFAULT_VIDEO_VOLUME = 0.6;

export class GallerySlideshow extends Hero{
    constructor(options) {
        super(options);

        this.galleryCta = this.el.querySelector('.gallery-button');
        this.videoCta = this.el.querySelector('.video-button');
        this.gallery = this.el.querySelector('.lightgallery');
        // if a video item is present, it's always the first item in the gallery, pushing the gallery index by 1
        this.videoIndex = 0;
        this.galleryIndex = this.gallery && this.gallery.querySelector('.video-item') ? 1 : 0;
        this._vimeoSdkPromise = null;

        this.handleVideoClick = this.handleVideoClick.bind(this);
        this.handleGalleryClick = this.handleGalleryClick.bind(this);
        this.handleSlideItemLoad = this.handleSlideItemLoad.bind(this);
    }

    initialize() {
        super.initialize();

        this.lightGallery = lightGallery(this.gallery,{
            zoom: true,
            mode: 'lg-slide',
            showZoomInOutIcons: true,
            speed: 350,
            plugins: [lgZoom, lgVideo],
            licenseKey: process.env.LIGHTGALLERY_LICENSE_KEY ?? '',
            mobileSettings: {
                controls: true,
                showCloseIcon: true,
                download: false
            },
            download: false,
            actualSize: false,
            hideScrollbar: true,
            appendSubHtmlTo: '.lg-item',
            // Autoplay the video slide as soon as the lightbox opens (it's always the first/only
            // video item). This is lightGallery's default behavior, and it's what already made
            // Asset (HTML5) videos start playing automatically - keep it explicit here.
            autoplayFirstVideo: true,
            // Browsers block autoplay-with-sound outright unless the video starts muted - so
            // YouTube/Vimeo have to start muted here. Once playback actually begins, our own
            // "onStateChange"/"play" handlers below (in applyVideoSettings) unmute and set the
            // volume via the player APIs - unmuting an already-playing video via script is not
            // subject to the same autoplay restriction, so this combination plays automatically
            // AND ends up audible at DEFAULT_VIDEO_VOLUME whenever the browser allows the unmute.
            youTubePlayerParams: { mute: 1 },
            vimeoPlayerParams: { muted: 1 },
        });

        // Must be registered AFTER the lightGallery instance (and its lgVideo plugin) exists:
        // lgVideo binds its own "lgSlideItemLoad" listener internally when initialized above, and
        // that's what actually appends the <video>/<iframe> element to the DOM. Registering our
        // listener beforehand (e.g. from addEvents(), which runs during super.initialize() above)
        // would fire before the video element exists, silently finding nothing to act on.
        if (this.gallery) {
            this.gallery.addEventListener('lgSlideItemLoad', this.handleSlideItemLoad);
        }
    }

    handleVideoClick() {
        this.lightGallery.openGallery(this.videoIndex);
    }

    handleGalleryClick() {
        this.lightGallery.openGallery(this.galleryIndex);
    }

    /**
     * "lgSlideItemLoad" fires before lightGallery has actually appended the <video>/<iframe>
     * element to the DOM (that append happens asynchronously inside its video plugin), so
     * querying for ".lg-video-object" synchronously here almost always finds nothing and the
     * handler silently no-ops. Instead, watch the slide for the element to actually appear.
     *
     * Note: not scoped to ".lg-current" - on the very first slide open, lightGallery hasn't
     * necessarily flagged the slide as current yet at the moment this fires, so that selector
     * can miss it. Since there's only ever one video item in this gallery, querying for
     * ".lg-video-object" directly (with no ".lg-current" prefix) is simpler and reliable.
     */
    handleSlideItemLoad() {
        // lightGallery renders the actual lightbox (".lg-container", with the video/iframe
        // inside it) into its own DOM tree appended to <body> - it is NOT nested inside
        // "this.gallery" (the source ".lightgallery" element just holds the data-src items).
        // So the video element has to be looked up - and watched for - in the document as a
        // whole, not scoped under this.gallery.
        const immediateVideoEl = document.querySelector('.lg-video-object');
        if (immediateVideoEl) {
            this.applyVideoSettings(immediateVideoEl);
            return;
        }

        if (this._videoWatcher) {
            this._videoWatcher.disconnect();
        }

        this._videoWatcher = new MutationObserver(() => {
            const videoEl = document.querySelector('.lg-video-object');
            if (!videoEl) {
                return;
            }
            this._videoWatcher.disconnect();
            this._videoWatcher = null;
            this.applyVideoSettings(videoEl);
        });
        this._videoWatcher.observe(document.body, { childList: true, subtree: true });

        // Safety net: stop watching after a few seconds so a stray observer never lingers
        // if the video element never shows up for some reason.
        setTimeout(() => {
            if (this._videoWatcher) {
                this._videoWatcher.disconnect();
                this._videoWatcher = null;
            }
        }, 5000);
    }

    /**
     * Applies a reliable size and the default starting volume to whichever video type just
     * loaded in the lightbox (self-hosted HTML5, YouTube, or Vimeo).
     */
    applyVideoSettings(videoEl) {
        // lightGallery's own JS-computed size for video slides is unreliable, especially on the
        // first open (it can fall back to the browser's default 300x150 iframe size). Force a
        // consistent, responsive size directly via JS with !important so it can't be overridden
        // by any inline style lightGallery sets before or after this runs.
        const videoCont = videoEl.closest('.lg-video-cont');
        const forceSize = () => {
            if (!videoCont) {
                return;
            }
            videoCont.style.setProperty('width', 'min(90vw, 1200px)', 'important');
            videoCont.style.setProperty('height', 'auto', 'important');
            videoCont.style.setProperty('aspect-ratio', '16 / 9', 'important');
        };
        forceSize();

        // lightGallery recalculates and overwrites the whole inline "style" attribute on its
        // own (e.g. once video metadata loads), wiping out what we just set above. Watch the
        // attribute and re-force our values whenever it changes, for as long as this video
        // slide stays in the DOM.
        if (videoCont) {
            if (this._sizeWatcher) {
                this._sizeWatcher.disconnect();
            }
            let restoring = false;
            this._sizeWatcher = new MutationObserver(() => {
                if (restoring) {
                    return;
                }
                const expected = 'width: min(90vw, 1200px) !important; height: auto !important; aspect-ratio: 16 / 9 !important;';
                if (videoCont.getAttribute('style') === expected) {
                    return;
                }
                restoring = true;
                forceSize();
                restoring = false;
            });
            this._sizeWatcher.observe(videoCont, { attributes: true, attributeFilter: ['style'] });
        }

        if (videoEl.classList.contains('lg-youtube')) {
            const setYoutubeVolume = () => {
                // mute/unMute and volume are separate states in the YouTube API - both are needed
                videoEl.contentWindow?.postMessage(JSON.stringify({event: 'command', func: 'unMute', args: ''}), '*');
                videoEl.contentWindow?.postMessage(JSON.stringify({event: 'command', func: 'setVolume', args: [DEFAULT_VIDEO_VOLUME * 100]}), '*');
            };
            // The YouTube embed already has enablejsapi=1 (set by lightGallery), so postMessage
            // commands work without loading the full YouTube IFrame API. Set it once shortly after
            // load, and again once the player actually starts playing - browsers/players can re-apply
            // their own remembered volume right when playback starts, overriding an earlier setting.
            setTimeout(setYoutubeVolume, 500);
            window.addEventListener('message', (e) => {
                if (typeof e.data !== 'string' || !e.data.includes('onStateChange')) {
                    return;
                }
                try {
                    const data = JSON.parse(e.data);
                    if (data.event === 'onStateChange' && data.info === 1) { // 1 = playing
                        setYoutubeVolume();
                    }
                } catch (err) { /* not a JSON message we care about */ }
            });
        } else if (videoEl.classList.contains('lg-vimeo')) {
            this.loadVimeoSdk()
                .then(() => {
                    const player = new window.Vimeo.Player(videoEl);
                    player.setVolume(DEFAULT_VIDEO_VOLUME);
                    // Re-apply once playback actually starts, in case the player resets volume then.
                    player.on('play', () => player.setVolume(DEFAULT_VIDEO_VOLUME));
                })
                .catch(() => {
                    // Vimeo SDK failed to load - not critical, video still plays at its own default volume.
                });
        } else if (videoEl.tagName === 'VIDEO') {
            const setHtml5Volume = () => {
                videoEl.volume = DEFAULT_VIDEO_VOLUME;
                videoEl.muted = false;
            };
            // Chrome (and others) can re-apply their own remembered volume for the site right when
            // playback starts, overriding whatever was set on load - reassert on "play" too.
            setHtml5Volume();
            videoEl.addEventListener('loadedmetadata', setHtml5Volume);
            videoEl.addEventListener('play', setHtml5Volume);
        }
    }

    /**
     * Lazily loads the Vimeo Player SDK once, only when a Vimeo video is actually opened.
     */
    loadVimeoSdk() {
        if (window.Vimeo) {
            return Promise.resolve();
        }

        if (!this._vimeoSdkPromise) {
            this._vimeoSdkPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://player.vimeo.com/api/player.js';
                script.async = true;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        return this._vimeoSdkPromise;
    }

    addEvents() {
        super.addEvents();

        if (this.videoCta) {
            this.videoCta.addEventListener('click', this.handleVideoClick);
        }
        if (this.galleryCta) {
            this.galleryCta.addEventListener('click', this.handleGalleryClick);
        }
    }

    removeEvents() {
        super.removeEvents();

        if (this.videoCta) {
            this.videoCta.removeEventListener('click', this.handleVideoClick);
        }
        if (this.galleryCta) {
            this.galleryCta.removeEventListener('click', this.handleGalleryClick);
        }
        if (this.gallery) {
            this.gallery.removeEventListener('lgSlideItemLoad', this.handleSlideItemLoad);
        }
        if (this._videoWatcher) {
            this._videoWatcher.disconnect();
            this._videoWatcher = null;
        }
        if (this._sizeWatcher) {
            this._sizeWatcher.disconnect();
            this._sizeWatcher = null;
        }
    }

}

Me.views['GallerySlideshow'] = GallerySlideshow;